export const VIEWPORT_MARGIN = 8;

export interface PopupPlacement {
   align: 'start' | 'center' | 'end';
   side: 'top' | 'bottom';
   sideOffset: number;
   /** At least as wide as the anchor, as a list of options under its field. */
   matchAnchorWidth?: boolean;
}

interface Bounds {
   top: number;
   bottom: number;
   left: number;
   right: number;
}

/**
 * Positions a fixed popup against its anchor, flipping to the roomier side
 * when the preferred one is too short. `--popup-transform-origin` points at
 * the anchor, for a popup that scales in from it.
 *
 * Returns false once the anchor has scrolled out of the visible bounds.
 */
export function placePopup(
   popup: HTMLElement,
   anchor: HTMLElement,
   { align, side, sideOffset, matchAnchorWidth = false }: PopupPlacement
) {
   const rect = anchor.getBoundingClientRect();
   const bounds = visibleBounds();
   if (rect.bottom < bounds.top || rect.top > bounds.bottom) return false;

   const maxWidth = bounds.right - bounds.left - 2 * VIEWPORT_MARGIN;
   if (matchAnchorWidth) popup.style.minWidth = `${Math.min(rect.width, maxWidth)}px`;
   popup.style.maxWidth = `${maxWidth}px`;

   // scrollHeight is the full height whatever max-height is in force, so
   // measuring never resets how far the popup is scrolled.
   const fullHeight = popup.scrollHeight + popup.offsetHeight - popup.clientHeight;
   const { placedBelow, space } = pickSide(rect, bounds, side, sideOffset, fullHeight);
   const height = Math.min(fullHeight, space);
   popup.style.maxHeight = `${space}px`;
   popup.style.top = `${placedBelow ? rect.bottom + sideOffset : rect.top - sideOffset - height}px`;
   popup.dataset.side = placedBelow ? 'bottom' : 'top';

   const width = popup.offsetWidth;
   const popupLeft = alignedLeft(anchor, rect, bounds, align, width);
   popup.style.left = `${popupLeft}px`;

   const originX = Math.min(Math.max(rect.left + rect.width / 2 - popupLeft, 0), width);
   popup.style.setProperty(
      '--popup-transform-origin',
      `${originX}px ${placedBelow ? 'top' : 'bottom'}`
   );
   return true;
}

/** The visual viewport: what a phone keyboard or pinch zoom leaves visible. */
export function visibleBounds(): Bounds {
   const viewport = window.visualViewport;
   const top = viewport?.offsetTop ?? 0;
   const left = viewport?.offsetLeft ?? 0;
   return {
      top,
      bottom: top + (viewport?.height ?? window.innerHeight),
      left,
      right: left + (viewport?.width ?? window.innerWidth),
   };
}

/**
 * The preferred side if a popup this tall fits there, else the roomier one,
 * with the room it has on the side it lands on.
 */
export function pickSide(
   rect: DOMRect,
   bounds: Bounds,
   side: PopupPlacement['side'],
   gap: number,
   height: number
) {
   const below = bounds.bottom - rect.bottom - gap - VIEWPORT_MARGIN;
   const above = rect.top - bounds.top - gap - VIEWPORT_MARGIN;
   const fitsPreferred = height <= (side === 'bottom' ? below : above);
   const preferredIsRoomier = side === 'bottom' ? below >= above : above >= below;
   const placedBelow = (side === 'bottom') === (fitsPreferred || preferredIsRoomier);
   return { placedBelow, space: Math.max(placedBelow ? below : above, 0) };
}

/** The popup's left edge for `align` in the anchor's reading direction, kept inside the bounds. */
export function alignedLeft(
   anchor: Element,
   rect: DOMRect,
   bounds: Bounds,
   align: PopupPlacement['align'],
   width: number
) {
   const rtl = getComputedStyle(anchor).direction === 'rtl';
   const alignLeft = align === 'start' ? !rtl : align === 'end' ? rtl : null;
   const aligned =
      alignLeft == null
         ? rect.left + (rect.width - width) / 2
         : alignLeft
           ? rect.left
           : rect.right - width;
   return Math.max(
      bounds.left + VIEWPORT_MARGIN,
      Math.min(aligned, bounds.right - VIEWPORT_MARGIN - width)
   );
}

/**
 * Calls `reposition` whenever the page, the visual viewport or the popup
 * changes size or scrolls, but not for a scroll inside the popup. It follows
 * rather than closes on a resize: on a phone the keyboard folding away resizes
 * the window just as a popup opens. Returns the function that stops it.
 */
export function followAnchor(popup: HTMLElement, reposition: () => void) {
   function handleScroll(event: Event) {
      if (event.target instanceof Node && popup.contains(event.target)) return;
      reposition();
   }

   const viewport = window.visualViewport;
   const popupResize =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(reposition);
   popupResize?.observe(popup);
   window.addEventListener('resize', reposition);
   window.addEventListener('scroll', handleScroll, { capture: true, passive: true });
   viewport?.addEventListener('resize', reposition);
   viewport?.addEventListener('scroll', reposition);
   return () => {
      popupResize?.disconnect();
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', handleScroll, { capture: true });
      viewport?.removeEventListener('resize', reposition);
      viewport?.removeEventListener('scroll', reposition);
   };
}
